import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import type { IFormElement } from '../types';
import { normalizeChoices } from '../utils/helpers';
import styles from './fields.module.css';

export interface ImagePickerFieldProps {
  element: IFormElement;
  questionNumber?: number;
}

export const ImagePickerField: React.FC<ImagePickerFieldProps> = ({
  element,
  questionNumber,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = errors[element.name];

  const choices = normalizeChoices(element.choices);
  const multiSelect = element.multiSelect || false;

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label
        className={`${styles.label} ${
          element.isRequired ? styles.required : ''
        }`}
      >
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <Controller
        name={element.name}
        control={control}
        defaultValue={multiSelect ? [] : ''}
        render={({ field }) => {
          const selectedValues: string[] = Array.isArray(field.value)
            ? field.value
            : field.value
            ? [String(field.value)]
            : [];

          const handleSelect = (value: string) => {
            if (multiSelect) {
              if (selectedValues.includes(value)) {
                field.onChange(selectedValues.filter((v) => v !== value));
              } else {
                field.onChange([...selectedValues, value]);
              }
            } else {
              field.onChange(value);
            }
          };

          return (
            <div className={styles.imagePickerGrid}>
              {choices.map((choice, index) => {
                const isSelected = selectedValues.includes(choice.value);

                return (
                  <div
                    key={index}
                    className={`${styles.imagePickerItem} ${
                      isSelected ? styles.selected : ''
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      !element.readOnly && handleSelect(choice.value)
                    }
                    onKeyDown={(e) => {
                      if (
                        (e.key === 'Enter' || e.key === ' ') &&
                        !element.readOnly
                      ) {
                        e.preventDefault();
                        handleSelect(choice.value);
                      }
                    }}
                  >
                    {choice.imageLink && (
                      <img
                        src={choice.imageLink}
                        alt={choice.text || choice.value}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    {choice.text && (
                      <div className={styles.imagePickerLabel}>
                        {choice.text}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        }}
      />
      {error && (
        <p className={styles.errorMessage}>{error.message as string}</p>
      )}
    </div>
  );
};

export default ImagePickerField;
